import { z } from "zod"

// Regex patterns for detecting email, phone number, and address
const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/
const phoneRegex = /\b(\+?\d{1,3}[\s.-]?)?\(?\d{2,4}\)?[\s.-]?\d{2,4}[\s.-]?\d{2,4}\b/

// Handle validate number
export const validateNumber = (e) => {
  const value = e.target?.value
  const allowedChars = /[^0-9]/g
  const filteredValue = value.replace(allowedChars, "")
  e.target.value = filteredValue
}

export const chatSchema = z.object({
  text: z
    .string()
    .min(1, { message: "Text is required" })
    .refine((val) => !emailRegex.test(val), {
      message: "Kindly do not share email address. This might lead to account deactivation."
    })
    .refine((val) => !phoneRegex.test(val), {
      message: "Kindly do not share phone no. This might lead to account deactivation."
    })
})

export const loginSchema = z.object({
  email: z.email("Please enter your email address"),

  "g-recaptcha-response": z.string().min(1, "Please verify reCAPTCHA"),

  password: z
    .string()
    .min(1, "Please enter your password")
})

export const signupSchema = z.object({
  email: z.email("Please enter a valid email address"),

  "g-recaptcha-response": z.string().min(1, "Please verify reCAPTCHA"),

  fullName: z
    .string()
    .min(1, "Fullname is required")
  ,

  username: z
    .string()
    .min(1, "Username is required")
  ,

  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(24, "Password must be at most 24 characters")
    .regex(
      /^(?=.*[0-9])(?=.*[\W_])[a-zA-Z0-9\W_]{8,24}$/,
      "Password must include at least one number and one special character"
    ),

  confPassword: z
    .string()
    .min(8, "Confirm password must be at least 8 characters")
    .max(24, "Confirm password must be at most 24 characters")
    .regex(
      /^(?=.*[0-9])(?=.*[\W_])[a-zA-Z0-9\W_]{8,24}$/,
      "Confirm password must include at least one number and one special character"
    ),
}).refine((data) => data.password === data.confPassword, {
  message: "Passwords do not match",
  path: ["confPassword"],
})

export const specialistSignupSchema = z.object({
  email: z.email("Please enter a valid email address"),

  "g-recaptcha-response": z.string().min(1, "Please verify reCAPTCHA"),

  fullName: z
    .string()
    .min(1, "FullName is required")
  ,

  phone: z.string().min(1, "Please enter phone number"),

  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(24, "Password must be at most 24 characters")
    .regex(
      /^(?=.*[0-9])(?=.*[\W_])[a-zA-Z0-9\W_]{8,24}$/,
      "Password must include at least one number and one special character"
    ),

  confPassword: z
    .string()
    .min(8, "Confirm password must be at least 8 characters")
    .max(24, "Confirm password must be at most 24 characters")
    .regex(
      /^(?=.*[0-9])(?=.*[\W_])[a-zA-Z0-9\W_]{8,24}$/,
      "Confirm password must include at least one number and one special character"
    ),
}).refine((data) => data.password === data.confPassword, {
  message: "Passwords do not match",
  path: ["confPassword"],
})

export const updatePasswordSchema = z.object({
  currentPassword: z
    .string()
    .min(1, "Please enter old password"),

  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(24, "Password must be at most 24 characters")
    .regex(
      /^(?=.*[0-9])(?=.*[\W_])[a-zA-Z0-9\W_]{8,24}$/,
      "Password must include at least one number and one special character"
    ),

  confPassword: z
    .string()
    .min(8, "Confirm password must be at least 8 characters")
    .max(24, "Confirm password must be at most 24 characters")
    .regex(
      /^(?=.*[0-9])(?=.*[\W_])[a-zA-Z0-9\W_]{8,24}$/,
      "Confirm password must include at least one number and one special character"
    ),
}).refine((data) => data.password === data.confPassword, {
  message: "Passwords do not match",
  path: ["confPassword"],
}).refine((data) => data.currentPassword !== data.password, {
  message: "New password cannot be the same as the old password",
  path: ["password"],
})

export const forgotPasswordSchema = z.object({
  email: z.email("Please enter a valid email address"),

  "g-recaptcha-response": z.string().min(1, "Please verify reCAPTCHA"),
})

export const resetPasswordSchema = z.object({
  "g-recaptcha-response": z.string().min(1, "Please verify reCAPTCHA"),

  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(24, "Password must be at most 24 characters")
    .regex(
      /^(?=.*[0-9])(?=.*[\W_])[a-zA-Z0-9\W_]{8,24}$/,
      "Password must include at least one number and one special character"
    ),

  confPassword: z
    .string()
    .min(8, "Confirm password must be at least 8 characters")
    .max(24, "Confirm password must be at most 24 characters")
    .regex(
      /^(?=.*[0-9])(?=.*[\W_])[a-zA-Z0-9\W_]{8,24}$/,
      "Confirm password must include at least one number and one special character"
    ),
}).refine((data) => data.password === data.confPassword, {
  message: "Passwords do not match",
  path: ["confPassword"],
})

export const contactUsSchema = z.object({
  email: z.email("Please enter a valid email address"),

  "g-recaptcha-response": z.string().min(1, "Please verify reCAPTCHA"),

  phone: z.string().min(1, "Please enter phone number"),

  interested: z.string().min(1, "Please enter interested in"),

  message: z.string().min(1, "Please enter message"),

  fullName: z
    .string()
    .min(8, "Full name must be at least 8 characters")
    .max(24, "Full name must be at most 24 characters"),
})

export const createForumSchema = z.object({
  title: z.string().min(1, "Please enter title"),

  banner: z.array(z.any()).nonempty("Please upload banner image"),

  categoryId: z.string().min(1, "Please select forum category"),

  description: z.string().min(10, "Descriptione must be at least 10 characters"),
})

export const updateDetailsSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),

  fullName: z.string().min(1, "Full Name is required"),

  username: z.string().min(1, "Username is required"),

  bloodGroup: z
    .string()
    .min(1, "Blood group is required")
    .refine(
      (val) =>
        [
          "A+",
          "A-",
          "B+",
          "B-",
          "AB+",
          "AB-",
          "O+",
          "O-",
        ].includes(val),
      { message: "Invalid blood group. Must be one of A+, A-, B+, B-, AB+, AB-, O+, O-" }
    ), phone: z
      .string()
      .min(1, "Phone number is required"),

  country: z.string().min(1, "Country is required"),

  state: z.string().min(1, "State is required"),

  city: z.string().min(1, "City is required"),

  street: z.string().min(1, "Street is required"),

  zip: z
    .string()
    .min(1, "Zip Code is required")
    .regex(/^\d{5,6}$/, "Zip code must be 5 or 6 digits")
});
export const webinarBasicsSchema = z.object({
  title: z
    .string()
    .min(1, { message: "Title is required" })
    .max(100, { message: "Title cannot exceed 100 characters" }),

  description: z.string().min(1, { message: "Description is required" }),

  numberOfSeats: z
    .number()
    .min(15, { message: "Number of seats must be at least 15" })
    .max(100000, { message: "Maximum seats cannot exceed 100,000" })
    .default(0),

  targetAudience: z.array(z.string()).min(1, { message: "Select at least one target audience" }),

  coverImage: z.array(z.any()).optional(),
})

export const webinarScheduleSchema = z.object({
  date: z.string(z.date()).min(1, "Date is required"),

  // webinarId: z.string().min(1, { message: "Webinar ID is required" }),

  duration: z.number().min(1, { message: "Duration is required" }),

  timeZone: z.string().min(1, { message: "Time zone is required" }),

  startTime: z.string().regex(/^\d{2}:\d{2}$/, { message: "Start time must be in HH:MM format" }),
})

export const webinarFormatSchema = z.object({
  // webinarId: z.string().min(1, "Webinar ID is required"),

  format: z.enum(["live", "recorded"], { message: "Please select a valid format" }),
  access: z.enum(["public", "private"], { message: "Please select a valid access" }),
})

export const webinarResourcesSchema = z.object({
  file: z.any().nullable().optional(),


  webinarLink: z
    .string({ required_error: "Webinar link is required" })
    .min(1, "Webinar link is required")
    .url("Invalid URL"),
})
export const ratingSchema = z.object({
  rating: z.number().min(1, "Rating required").max(5, "Rating cannot exceed 5"),
  description: z
    .string()
    .min(5, "Feedback must be at least 5 characters")
    .max(100, "Feedback cannot exceed 100 characters")
});
